"use client";

import { prettyNumber } from "@/lib/string-utils";
import type { DetailedListingListItem } from "@/lib/types";
import {
  LISTING_ATTRIBUTE_BOOST_VALUE,
  LISTING_ATTRIBUTE_WEIGHT_TABLE,
  sbaPrequalificationAvailableInCountry,
  type ListingInfoAttribute,
} from "@repo/utils";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

export type ListingPreviewInfoProps = {
  listing: DetailedListingListItem;
  boostedAttributes?: ListingInfoAttribute[];
  className?: string;
};

type FinancialMetric = {
  key: ListingInfoAttribute;
  label: string;
  value: string;
  weight: number;
};

type QualifierBadge = {
  key: string;
  label: string;
  weight: number;
};

function isValidFinancial(
  val: number | bigint | null | undefined,
): val is number | bigint {
  if (val == null) return false;
  const n = typeof val === "bigint" ? Number(val) : val;
  return Number.isFinite(n) && n > 1;
}

/**
 * One-line summary under the price: up to two financial facts, then
 * qualifiers (financing, SBA, property, owner hours, sale type) as plain
 * text separated by dots. Always renders exactly one line so cards in a grid
 * share the same height; falls back to "Financials on request".
 */
export function ListingPreviewInfo({
  listing,
  boostedAttributes,
  className,
}: ListingPreviewInfoProps) {
  const t = useTranslations();

  const attributeWeightTable = useMemo(() => {
    const table = { ...LISTING_ATTRIBUTE_WEIGHT_TABLE };

    if (boostedAttributes?.length) {
      for (const attribute of boostedAttributes) {
        table[attribute] += LISTING_ATTRIBUTE_BOOST_VALUE;
      }
    }

    return table;
  }, [boostedAttributes]);

  const financials = useMemo<FinancialMetric[]>(() => {
    const output: FinancialMetric[] = [];

    if (isValidFinancial(listing.revenue)) {
      output.push({
        key: "REVENUE",
        label: t("saas.listingPreview.info.revenueLabel"),
        value: `$${prettyNumber(listing.revenue)}`,
        weight: attributeWeightTable.REVENUE,
      });
    }

    if (isValidFinancial(listing.profit)) {
      output.push({
        key: "PROFIT",
        label: t("saas.listingPreview.info.profitLabel"),
        value: `$${prettyNumber(listing.profit)}`,
        weight: attributeWeightTable.PROFIT,
      });
    }

    if (isValidFinancial(listing.sde)) {
      output.push({
        key: "SDE",
        label: t("saas.listingPreview.info.sdeLabel"),
        value: `$${prettyNumber(listing.sde)}`,
        weight: attributeWeightTable.SDE,
      });
    }

    if (isValidFinancial(listing.ebitda)) {
      output.push({
        key: "EBITDA",
        label: t("saas.listingPreview.info.ebitdaLabel"),
        value: `$${prettyNumber(listing.ebitda)}`,
        weight: attributeWeightTable.EBITDA,
      });
    }

    if (isValidFinancial(listing.ffande)) {
      output.push({
        key: "FFANDE",
        label: t("saas.listingPreview.info.ffandeLabel"),
        value: `$${prettyNumber(listing.ffande)}`,
        weight: attributeWeightTable.FFANDE,
      });
    }

    if (isValidFinancial(listing.inventory)) {
      output.push({
        key: "INVENTORY",
        label: t("saas.listingPreview.info.inventoryLabel"),
        value: `$${prettyNumber(listing.inventory)}`,
        weight: attributeWeightTable.INVENTORY,
      });
    }

    if (isValidFinancial(listing.minimumDownPayment)) {
      output.push({
        key: "MINIMUM_DOWN_PAYMENT",
        label: t("saas.listingPreview.info.downPaymentLabel"),
        value: `$${prettyNumber(listing.minimumDownPayment)}`,
        weight: attributeWeightTable.MINIMUM_DOWN_PAYMENT,
      });
    }

    return output.sort((a, b) => b.weight - a.weight);
  }, [listing, attributeWeightTable, t]);

  const qualifiers = useMemo<QualifierBadge[]>(() => {
    const output: QualifierBadge[] = [];

    if (
      listing.sbaPrequalified &&
      sbaPrequalificationAvailableInCountry(
        listing.business?.address?.country?.code,
      )
    ) {
      output.push({
        key: "sba",
        label: t("saas.listingPreview.info.sbaBadge"),
        weight: attributeWeightTable.SBA_PREQUALIFIED,
      });
    }

    if (listing.financingAvailable) {
      output.push({
        key: "financing",
        label: t("saas.listingPreview.info.financingBadge"),
        weight: attributeWeightTable.FINANCING_AVAILABLE,
      });
    }

    if (listing.propertyIncludedInPrice) {
      output.push({
        key: "property",
        label: t("saas.listingPreview.info.propertyBadge"),
        weight: attributeWeightTable.PROPERTY_INCLUDED,
      });
    }

    if (listing.ownerWeeklyHours) {
      output.push({
        key: "hours",
        label: t("saas.listingPreview.info.ownerHoursBadge", {
          hours: listing.ownerWeeklyHours,
        }),
        weight: attributeWeightTable.OWNER_WEEKLY_HOURS,
      });
    }

    if (listing.saleType && listing.saleType !== "STOCK_SALE") {
      output.push({
        key: "saleType",
        label: t(`saas.listing.details.saleTypeValues.${listing.saleType}`),
        weight: 0,
      });
    }

    return output.sort((a, b) => b.weight - a.weight);
  }, [listing, attributeWeightTable, t]);

  const displayedFinancials = financials.slice(0, 2);
  const isEmpty = displayedFinancials.length === 0 && qualifiers.length === 0;

  // Fixed one-line height so every card shares the same footprint.
  const lineClassName = cn(
    "h-5 truncate text-xs leading-5 text-muted-foreground",
    className,
  );

  if (isEmpty) {
    return (
      <p className={lineClassName}>
        {t("saas.listingPreview.info.noFinancials")}
      </p>
    );
  }

  const parts: { key: string; node: React.ReactNode }[] = [
    ...displayedFinancials.map((metric) => ({
      key: metric.key,
      node: (
        <>
          {metric.label}{" "}
          <span className="font-data font-semibold text-foreground">
            {metric.value}
          </span>
        </>
      ),
    })),
    ...qualifiers.map((q) => ({ key: q.key, node: q.label })),
  ];

  return (
    <p
      className={lineClassName}
      title={[
        ...displayedFinancials.map((m) => `${m.label} ${m.value}`),
        ...qualifiers.map((q) => q.label),
      ].join(" · ")}
    >
      {parts.map((part, i) => (
        <span key={part.key}>
          {i > 0 && <span className="mx-1 text-[#B0B0B0]">·</span>}
          {part.node}
        </span>
      ))}
    </p>
  );
}
